import type { ResponsiveFormLayout } from "../types";

/**
 * Extract all field names from a Responsive Form layout.
 *
 * This is useful for determining which fields are explicitly included in the form,
 * even if their property metadata has isHidden=true. When a field is included in
 * the form layout, it should be displayed regardless of the property's hidden setting.
 *
 * @param formLayout The Responsive Form layout from Aras
 * @returns Set of field names that are explicitly included in the layout
 */
export function extractFieldNamesFromLayout(
  formLayout: ResponsiveFormLayout | null | undefined,
): Set<string> {
  const fieldNames = new Set<string>();

  if (!formLayout?.layout || !formLayout.order) {
    return fieldNames;
  }

  const { layout, order } = formLayout;

  // Recursively extract field names from layout structure
  const extractFromElement = (elementId: string): void => {
    const element = layout[elementId];

    if (!element) {
      // Not in layout dictionary means it's a field name
      fieldNames.add(elementId);
      return;
    }

    // Recursively process children
    for (const childId of element.children) {
      extractFromElement(childId);
    }
  };

  // Process all root elements in order
  for (const rootId of order) {
    extractFromElement(rootId);
  }

  return fieldNames;
}
